import React from 'react';
import { ArrowRight } from 'lucide-react';
import { weekendLineup } from '../data/popups';
import type { LineupWeekend, PopupDay } from '../types/popup';
import { SkewTag } from './SkewTag';
export function PopupLineup() {
  const [now, ...upcoming] = weekendLineup;
  return <section id="popups" aria-labelledby="popups-heading" className="bg-foodTeal text-foodCream border-b-4 border-foodMaroon py-20 lg:py-24" data-id="element-284" data-mp="2zfhbm4zenl6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-285" data-mp="70qxl11ytwgp">
        <header className="max-w-3xl mb-14" data-id="element-286" data-mp="bzbxt0h7wg55">
          <h2 id="popups-heading" className="font-wide text-[1.75rem] sm:text-5xl xl:text-6xl uppercase leading-[0.95] text-foodGold" data-id="element-287" data-mp="by0t8yfy74cz">
            <span className="block whitespace-nowrap" data-id="element-288" data-mp="lolsmo3g4cwy">Weekend Pop-Ups,</span>
            <span className="block" data-id="element-289" data-mp="qpt00m1ofa33">Leveled Up</span>
          </h2>
          <p className="text-lg mt-5 text-foodCream/90" data-id="element-290" data-mp="uql99fajtezd">
            New faces, new flavors, every other week. the Bay Area's favorite chefs take
            over the Pavilion{' '}
            <span className="font-bold text-foodGold" data-id="element-291" data-mp="3gya3u0wao4aq">
              Saturday & Sunday, 12 PM – 6 PM.
            </span>{' '}
            Come meet them, grab a plate, and enjoy the amazing India Basin view.
          </p>
        </header>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch" data-id="element-292" data-mp="323lz4oypx9w6">
          <NowCard weekend={now} data-id="element-293" data-mp="325gm08tjvc2w" />
          <ol className="lg:col-span-5 grid gap-8" aria-label="Coming up" data-id="element-294" data-mp="3m014xebqoj8">
            {upcoming.map(w => <TeaseCard key={w.name} weekend={w} data-id="element-295" data-mp="3ussxnny69zhf" />)}
          </ol>
        </div>
      </div>
    </section>;
}

/** This weekend is the Pavilion's own — full brand palette. */
function NowCard({
  weekend
}: {
  weekend: LineupWeekend;
}) {
  return <article className="lg:col-span-7 bg-foodCream text-foodMaroon border-4 border-foodMaroon shadow-[8px_8px_0px_0px_#F6B303] p-8 sm:p-10 flex flex-col" data-id="element-296" data-mp="mby44z39lrg0">
      <SkewTag tone="orange" shadow="espresso" data-id="element-297" data-mp="hjojnykx5jl4">
        {weekend.when}
      </SkewTag>
      <p className="font-display font-wide font-extrabold uppercase text-foodTeal mt-6" data-id="element-298" data-mp="2bu4vl34hpv32">
        {formatDays(weekend.days)}
      </p>
      <h3 className="font-wide text-4xl sm:text-5xl uppercase leading-[0.95] mt-2" data-id="element-299" data-mp="2gu99qmhxedq4">
        {weekend.name}
      </h3>
      <p className="text-lg mt-4 max-w-md" data-id="element-300" data-mp="272dcterkstnh">{weekend.note}</p>
      {weekend.href && <a href={weekend.href} className="mt-auto pt-8 inline-flex items-center gap-2 font-display font-wide font-extrabold uppercase text-foodOrange hover:text-foodMaroon transition-colors duration-150" data-id="element-301" data-mp="2700kq8s3t2wp">
          See what's cooking
          <ArrowRight size={20} aria-hidden="true" data-id="element-302" data-mp="1j5ppj63c6n3v" />
        </a>}
    </article>;
}

/**
 * Upcoming vendors stay deliberately quiet: no vendor colors, grayscale logo,
 * so their brand doesn't compete with the current weekend.
 */
function TeaseCard({
  weekend
}: {
  weekend: LineupWeekend;
}) {
  return <li className="border-2 border-dashed border-foodCream/40 p-6 sm:p-7 flex items-center gap-6" data-id="element-303" data-mp="3ih0e77q6tnsb">
      {weekend.logo && <div className="shrink-0 w-24 h-24 bg-white flex items-center justify-center" data-id="element-304" data-mp="16ru2f31a0jrk">
          <img src={weekend.logo} alt={`${weekend.name} logo`} className="max-w-full max-h-full grayscale opacity-60 mix-blend-multiply" data-id="element-305" data-mp="11szdrkgmo0zj" />
        </div>}
      <div data-id="element-306" data-mp="1brf3pk3x547g">
        <p className="text-sm font-bold uppercase tracking-wide text-foodGold" data-id="element-307" data-mp="1pko816d0ezj4">
          {weekend.when}
        </p>
        <h3 className="font-wide text-2xl sm:text-3xl uppercase leading-none mt-1" data-id="element-308" data-mp="1km38781o6hv7">
          {weekend.name}
        </h3>
        <p className="font-bold mt-2 text-foodCream/90" data-id="element-309" data-mp="1zhsznlpmjn7b">{formatDays(weekend.days)}</p>
        <p className="text-foodCream/70 mt-1" data-id="element-310" data-mp="1ujhnv5frr2mg">{weekend.note}</p>
      </div>
    </li>;
}
function formatDays(days: PopupDay[]) {
  if (days.length <= 2) {
    return days.map(d => `${d.weekday} ${d.date}`).join(' · ');
  }
  // Biweekly runs: group into Sat–Sun weekends, e.g. "Oct 24–25 & Oct 31–Nov 1"
  const weekends: string[] = [];
  for (let i = 0; i < days.length; i += 2) {
    const start = days[i].date;
    const end = days[i + 1]?.date;
    if (!end) {
      weekends.push(start);
      continue;
    }
    const [startMonth] = start.split(' ');
    const [endMonth, endDay] = end.split(' ');
    weekends.push(startMonth === endMonth ? `${start}–${endDay}` : `${start}–${end}`);
  }
  return `Sat & Sun · ${weekends.join(' & ')}`;
}